import { List } from '@gonfalon/immutable-shim/immutableShim';
import { createFormState } from '@gonfalon/legacy-forms/formUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { createImmutableState } from '@gonfalon/legacy-immutable/immutableUtils';
import { combineReducers } from 'redux';

import type { AccountAction } from '#actions/account';
import type { FormAction } from '#actions/forms';
import registry from '#reducers/registry';
import type { GlobalState } from '#reducers/state';
import type { Account } from '#utils/accounts/records/Account';
import type { RequestSeatsFormType } from '#utils/accounts/records/BulkInvites';
import { createNewRequestSeatsForm } from '#utils/accounts/records/BulkInvites';
import type { OwnerInfo } from '#utils/accounts/records/Owner';
import { createOwnerRecord } from '#utils/accounts/records/Owner';
import { createSignupForm } from '#utils/accounts/records/SignupForm';
import type { RequestAction } from '#utils/requestUtils';
import type { SignupOutcome } from '#utils/signupOutcome';

import '#epics/account';

const initialFormState = createImmutableState({
  form: createFormState(createSignupForm()),
  isUnverifiedMemberCreated: false,
  signupOutcome: undefined as SignupOutcome | undefined,
});

export function createAccountForm(state = initialFormState, action: AccountAction | FormAction) {
  switch (action.type) {
    case 'account/EDIT_NEW_ACCOUNT':
      return state.update('form', (f) => f.trackField(action.field).revalidate(action.account));
    case 'account/CREATE_UNVERIFIED_MEMBER':
      return state.update('form', (f) => f.submitting());
    case 'account/CREATE_UNVERIFIED_MEMBER_FAILED':
      return state.update('form', (f) => f.submitFailed(action.account, action.error));
    case 'account/CREATE_UNVERIFIED_MEMBER_DONE':
      return state.update('isUnverifiedMemberCreated', () => true).set('signupOutcome', action.signupOutcome);
    case 'forms/BLUR':
      if (action.model !== 'createAccountForm') {
        return state;
      }
      return state.update('form', (f) => f.handleBlur(action.field, f.modified));
    default:
      return state;
  }
}

type AccountEntityState = ImmutableMap<{
  entity?: Account;
  lastFetched: null | number;
  isFetching: boolean;
}>;

function accountEntity(
  state: AccountEntityState = createImmutableState({
    entity: undefined,
    lastFetched: null,
    isFetching: false,
  }),
  action: AccountAction,
) {
  switch (action.type) {
    case 'account/REQUEST_ACCOUNT':
      return state.set('isFetching', true);
    case 'account/REQUEST_ACCOUNT_DONE':
      return state.merge({
        isFetching: false,
        lastFetched: action.timestamp,
        entity: action.account,
      });
    case 'account/UPDATE_ACCOUNT_DONE':
      return state.merge({
        isFetching: false,
        lastFetched: action.timestamp,
        entity: action.account,
      });
    case 'account/DELETE_ACCOUNT_TOKEN_DONE':
      return state.setIn(['entity', 'tokens'], List());
    default:
      return state;
  }
}

export const account = combineReducers({
  entity: accountEntity,
});

const initialOwnerFormState = (owner = createOwnerRecord()) => createFormState(owner);

export function updateOwnerForm(state = initialOwnerFormState(), action: RequestAction) {
  if (action.model && action?.model !== 'updateOwnerForm') {
    return state;
  }
  switch (action.type) {
    case 'account/UPDATE_ACCOUNT_OWNER':
      return state.submitting();
    case 'account/UPDATE_ACCOUNT_OWNER_DONE':
      return state.submitted(action.promotedMember);
    case 'account/UPDATE_ACCOUNT_OWNER_FAILED':
      return state.submitFailed(action.promotedMember, action.error);
    case 'account/SELECT_ACCOUNT_OWNER':
      const { field, value } = action;
      const modified = state.modified.set(field, value);
      return state.trackField(field).revalidate(modified);
    case 'forms/BLUR':
      return state.handleBlur(action.field, state.modified);
    case 'forms/DESTROY':
      // Keep the form intact if we were redirected to beastmode escalation
      if (window.location.pathname === '/settings/escalate') {
        return state;
      }
      return initialOwnerFormState();
    case 'forms/INITIALIZE':
      if (action.model && action.model !== 'updateOwnerForm') {
        return state;
      }
      return initialOwnerFormState(action.initialState as OwnerInfo);
    default:
      return state;
  }
}

const initialSeatsRequestFormState = (request = createNewRequestSeatsForm()) => createFormState(request);

export function createRequestSeatsForm(state = initialSeatsRequestFormState(), action: AccountAction | FormAction) {
  const formKey = 'createRequestSeatsForm';
  switch (action.type) {
    case 'account/EDIT_SEAT_REQUEST_FIELD':
      return state.trackField(action.field).revalidate(action.seatsRequest);
    case 'account/CREATE_SEATS_REQUEST':
      return state.submitting();
    case 'account/CREATE_SEATS_REQUEST_FAILED':
      return state.submitFailed(action.seatsRequest, action.error);
    case 'account/CREATE_SEATS_REQUEST_DONE':
      return state.submitted(action.seatsRequest);
    case 'forms/BLUR':
      if (action.model !== formKey) {
        return state;
      }
      return state.handleBlur(action.field, state.modified);
    case 'forms/INITIALIZE':
      if (action.model !== formKey) {
        return state;
      }
      return initialSeatsRequestFormState(createNewRequestSeatsForm(action.initialState as RequestSeatsFormType));
    default:
      return state;
  }
}

export const updateOwnerFormSelector = (state: GlobalState) => state.updateOwnerForm;
export const createAccountFormSelector = (state: GlobalState) => state.createAccountForm.get('form');
export const createRequestSeatFormSelector = (state: GlobalState) => state.createRequestSeatsForm;
export const isUnverifiedMemberCreatedSelector = (state: GlobalState) =>
  state.createAccountForm.get('isUnverifiedMemberCreated');
export const signupOutcomeSelector = (state: GlobalState) =>
  state.createAccountForm.get('signupOutcome') as SignupOutcome | undefined;
export const accountSelector = (state: GlobalState) => state.account.entity;

export const sessionConfigSelector = (state: GlobalState) => accountSelector(state).getIn(['entity', 'sessionConfig']);

registry.addReducers({
  account,
  createAccountForm,
  createRequestSeatsForm,
  updateOwnerForm,
});

declare module '#reducers/state' {
  interface GlobalState {
    account: ReturnType<typeof account>;
    createAccountForm: ReturnType<typeof createAccountForm>;
    createRequestSeatsForm: ReturnType<typeof createRequestSeatsForm>;
    updateOwnerForm: ReturnType<typeof updateOwnerForm>;
  }
}
